import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardContent, CardHeader, Textarea } from '@/components/ui';
import { ontologyApi, type SparqlAskResult, type SparqlSelectResult } from '@/lib/api';
import { useApiError } from '@/lib/i18n/useApiError';

const EXAMPLES = [
  {
    label: 'List all projects',
    query: `PREFIX ett: <https://energytransition.kg/ontology#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT ?project ?label WHERE {
  GRAPH <https://energytransition.kg/graph/client> {
    ?project a ett:Project ;
             rdfs:label ?label .
  }
} LIMIT 50`,
  },
  {
    label: 'Geo-located entities',
    query: `PREFIX ett: <https://energytransition.kg/ontology#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT ?iri ?label ?lat ?lon WHERE {
  GRAPH <https://energytransition.kg/graph/client> {
    ?iri ett:latitude ?lat ;
         ett:longitude ?lon .
    OPTIONAL { ?iri rdfs:label ?label }
  }
}`,
  },
  {
    label: 'Developer / project map',
    query: `PREFIX ett: <https://energytransition.kg/ontology#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT ?dev ?project WHERE {
  GRAPH <https://energytransition.kg/graph/client> {
    ?project ett:hasDeveloper ?dev .
    OPTIONAL { ?dev rdfs:label ?devLabel }
  }
}`,
  },
];

/**
 * Read-only SPARQL editor for analysts.
 *
 * Plain textarea on purpose — Monaco would add ~3 MB to the bundle for a
 * surface we don't yet rely on heavily. If the team starts living in this
 * screen we'll swap to a Monaco-based editor with ontology-aware autocomplete.
 *
 * The backend enforces read-only (mutation keywords rejected) and caps
 * results at 500 rows.
 */
export function SparqlEditor() {
  const { t } = useTranslation(['studio', 'common']);
  const apiError = useApiError();
  const [query, setQuery] = useState(EXAMPLES[0]!.query);
  const [activeResult, setActiveResult] = useState<SparqlSelectResult | SparqlAskResult | null>(null);

  const run = useMutation({
    mutationFn: (q: string) => ontologyApi.runSparql(q),
    onSuccess: (r) => setActiveResult(r),
  });

  return (
    <div className="grid h-[calc(100vh-3.5rem)] grid-rows-[auto_1fr] gap-3 p-3">
      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">{t('studio:sparql.title')}</h2>
          <p className="mt-1 text-small text-slate-600">{t('studio:sparql.subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.label}
                type="button"
                onClick={() => setQuery(ex.query)}
                className="rounded border border-slate-200 px-2 py-1 text-small text-slate-700 hover:bg-slate-50"
              >
                {ex.label}
              </button>
            ))}
          </div>
          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={10}
            className="font-mono text-small"
            spellCheck={false}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => run.mutate(query)}
              disabled={!query.trim() || run.isPending}
            >
              {run.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Play className="mr-1 h-4 w-4" aria-hidden />
              )}
              {t('studio:sparql.run')}
            </Button>
            {run.isError && (
              <span className="flex items-center gap-1 text-small text-red-700">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                {apiError(run.error)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <h3 className="text-h3 text-brand-900">{t('studio:sparql.results')}</h3>
        </CardHeader>
        <CardContent className="h-full overflow-auto p-0">
          {!activeResult && (
            <p className="p-4 text-small text-slate-500">{t('studio:sparql.runPrompt')}</p>
          )}
          {activeResult?.kind === 'ask' && (
            <p className="p-4 text-body text-slate-700">
              <span className="font-mono">{activeResult.boolean ? 'true' : 'false'}</span>
            </p>
          )}
          {activeResult?.kind === 'select' && (
            <ResultTable result={activeResult} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ResultTable({ result }: { result: SparqlSelectResult }) {
  const { t } = useTranslation(['studio', 'common']);
  if (result.rows.length === 0) {
    return <p className="p-4 text-small text-slate-500">{t('common:status.noResults')}</p>;
  }
  return (
    <div>
      {result.truncated && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-small text-amber-800">
          {t('studio:sparql.truncated', { limit: 500 })}
        </div>
      )}
      <table className="w-full text-small">
        <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
          <tr>
            {result.columns.map((c) => (
              <th key={c} className="px-3 py-2 text-left font-mono">
                ?{c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {result.rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-slate-50">
              {result.columns.map((c) => (
                <td key={c} className="break-all px-3 py-1.5 font-mono text-slate-700">
                  {row.values[c] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
